import React, { useState, useEffect } from 'react'
import { Play, Trash2, Eye, Calendar, HardDrive, AlertCircle, Download, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'

const VideosTab = ({ onStatsChange }) => {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    loadVideos()
  }, [])

  const loadVideos = async () => {
    setLoading(true)
    try {
      // Use consolidated token system
      const token = localStorage.getItem('token')
      
      if (!token) {
        console.error('No authentication token found')
        toast.error('Authentication required')
        return
      }
      
      console.log('Loading videos with token type:', token.substring(0, 20) + '...')
      
      const response = await fetch('/.netlify/functions/videos-list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      const data = await response.json()
      if (data.success && data.videos) {
        setVideos(data.videos)
      } else {
        console.error('Failed to load videos:', data.message)
        toast.error('Failed to load videos')
      }
    } catch (error) {
      console.error('Error loading videos:', error)
      toast.error('Error loading videos')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteVideo = async (videoId, videoTitle) => {
    if (deleting) return
    
    setDeleting(videoId)
    try {
      // Use consolidated token system
      const token = localStorage.getItem('token')
      
      if (!token) {
        toast.error('Authentication required')
        setDeleting(null)
        return
      }
      
      const response = await fetch(`/.netlify/functions/video-delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ videoId })
      })
      
      const data = await response.json()
      if (data.success) {
        toast.success(`"${videoTitle}" deleted successfully`)
        setVideos(videos.filter(v => v.id !== videoId))
        setShowDeleteConfirm(null)
        // Refresh admin stats after deletion
        if (onStatsChange) {
          onStatsChange()
        }
      } else {
        toast.error(`Failed to delete video: ${data.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error deleting video:', error)
      toast.error('Error deleting video')
    } finally {
      setDeleting(null)
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown'
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Check if video likely needs conversion based on filename and mime type
  const needsConversion = (video) => {
    const problematicExtensions = ['.mov', '.avi', '.mkv', '.wmv', '.flv']
    const problematicMimeTypes = ['video/quicktime', 'video/x-msvideo', 'video/x-matroska']

    const filename = video.filename || ''
    const mimeType = video.mime_type || video.mimeType || ''

    return problematicExtensions.some(ext => filename.toLowerCase().endsWith(ext)) ||
           problematicMimeTypes.some(type => mimeType.includes(type)) ||
           mimeType.includes('hevc') || mimeType.includes('h265')
  }

  // Download video from R2 for admin conversion
  const handleDownloadVideo = async (video) => {
    setDownloading(video.id)
    try {
      const token = localStorage.getItem('token')

      // Get download URL from our API
      const response = await fetch(`/.netlify/functions/r2-download-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          objectKey: `videos/${video.cloudflare_uid}/${video.filename}`
        })
      })

      const data = await response.json()
      if (data.success && data.downloadUrl) {
        // Force download by fetching blob and creating download link
        const response = await fetch(data.downloadUrl)
        const blob = await response.blob()

        // Create blob URL and download
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = video.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up blob URL
        URL.revokeObjectURL(blobUrl)

        toast.success(`Downloaded ${video.filename} for conversion`)
      } else {
        throw new Error(data.message || 'Failed to get download URL')
      }
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download video')
    } finally {
      setDownloading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-netflix-red mx-auto mb-4"></div>
          <p className="text-gray-400">Loading videos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Video Management</h2>
        <div className="flex space-x-2">
          <button
            onClick={loadVideos}
            className="btn-secondary text-sm"
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Admin Conversion Service Info */}
      <div className="card p-4 bg-blue-900/20 border-blue-500/30">
        <div className="flex items-start space-x-3">
          <RefreshCw className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-300 mb-1">Admin Video Conversion Service</h3>
            <p className="text-xs text-blue-200 mb-2">
              Videos marked "Needs Conversion" may not play properly in browsers. Use the download button to get the original file,
              convert with external tools (like Prism), then re-upload the converted version.
            </p>
            <div className="text-xs text-blue-300">
              <span className="font-medium">Workflow:</span> Download → Convert to H.264 MP4 → Re-upload → User gets working video
            </div>
          </div>
        </div>
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-12">
          <Play className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No Videos Found</h3>
          <p className="text-gray-400">No videos have been uploaded yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-gray-400 mb-4">
            Total videos: {videos.length}
          </div>
          
          {videos.map((video) => (
            <div key={video.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <Play className="w-5 h-5 text-netflix-red" />
                    <h3 className="text-lg font-semibold text-white">{video.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      video.visibility === 'private'
                        ? 'bg-red-900 text-red-200'
                        : video.visibility === 'restricted'
                        ? 'bg-yellow-900 text-yellow-200'
                        : 'bg-green-900 text-green-200'
                    }`}>
                      {video.visibility}
                    </span>
                    {needsConversion(video) && (
                      <span className="px-2 py-1 text-xs rounded-full bg-orange-900 text-orange-200 flex items-center space-x-1">
                        <RefreshCw className="w-3 h-3" />
                        <span>Needs Conversion</span>
                      </span>
                    )}
                  </div>
                  
                  {video.description && (
                    <p className="text-gray-300 mb-3 line-clamp-2">{video.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center space-x-2 text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(video.created_at)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-400">
                      <HardDrive className="w-4 h-4" />
                      <span>{formatFileSize(video.file_size)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-400">
                      <Eye className="w-4 h-4" />
                      <span>{video.category || 'Uncategorized'}</span>
                    </div>
                    <div className="text-gray-400">
                      <span className="font-mono text-xs">{video.cloudflare_uid}</span>
                    </div>
                  </div>
                  
                  {video.tags && video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {video.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  {/* Admin Download Button */}
                  <button
                    onClick={() => handleDownloadVideo(video)}
                    disabled={downloading === video.id}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Download for conversion"
                  >
                    {downloading === video.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </button>

                  <button
                    onClick={() => setShowDeleteConfirm(video.id)}
                    disabled={deleting === video.id}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete video"
                  >
                    {deleting === video.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Delete Confirmation */}
              {showDeleteConfirm === video.id && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center space-x-2 text-red-400 mb-3">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Confirm Deletion</span>
                  </div>
                  <p className="text-gray-300 mb-4">
                    Are you sure you want to delete "{video.title}"? This action cannot be undone.
                  </p>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleDeleteVideo(video.id, video.title)}
                      disabled={deleting === video.id}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      {deleting === video.id ? 'Deleting...' : 'Delete Video'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default VideosTab